import { useEffect, useRef, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface MenuItem {
  label: string;
  hint?: string | undefined;
  onSelect: () => void;
  disabled?: boolean | undefined;
  danger?: boolean | undefined;
}

const ITEM_HEIGHT = 40;

/**
 * A button that opens a short list of actions — a row's Archive and Delete, a page's less common moves.
 * The panel is fixed to the viewport rather than to the button, because the rows it serves sit inside
 * tables that scroll sideways, and an absolutely placed panel there is clipped by the scroll box.
 */
export function Menu({
  label,
  trigger,
  items,
  variant = "secondary",
  size = "md",
  iconOnly = false,
  className,
}: {
  label: string;
  trigger: ReactNode;
  /** The trigger is an icon, so the button needs the label as its name; a worded trigger names itself. */
  iconOnly?: boolean;
  items: (MenuItem | "divider")[];
  variant?: "secondary" | "ghost";
  size?: "sm" | "md";
  className?: string;
}) {
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top?: number; bottom?: number; right: number } | null>(null);

  useEffect(() => {
    if (!place) return;
    const close = () => setPlace(null);
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!panel.current?.contains(target) && !button.current?.contains(target)) close();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        button.current?.focus();
      }
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    // Fixed to the viewport, so any scroll would leave it floating over the wrong row.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [place]);

  function toggle() {
    if (place || !button.current) {
      setPlace(null);
      return;
    }
    const r = button.current.getBoundingClientRect();
    const right = window.innerWidth - r.right;
    // Opens upward when the list would run off the bottom of the window.
    const tall = items.length * ITEM_HEIGHT + 8;
    setPlace(
      r.bottom + tall > window.innerHeight
        ? { bottom: window.innerHeight - r.top + 4, right }
        : { top: r.bottom + 4, right },
    );
  }

  return (
    <>
      <Button
        ref={button}
        type="button"
        variant={variant}
        size={size}
        aria-haspopup="menu"
        aria-expanded={place !== null}
        aria-label={iconOnly ? label : undefined}
        className={className}
        onClick={toggle}
      >
        {trigger}
      </Button>
      {place && (
        <div
          ref={panel}
          role="menu"
          aria-label={label}
          style={place}
          className="border-bank-border-strong bg-bank-surface fixed z-40 w-64 rounded-lg border p-1 shadow-lg"
        >
          {items.map((item, i) =>
            item === "divider" ? (
              <div key={`divider-${i}`} role="separator" className="bg-bank-border my-1 h-px" />
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setPlace(null);
                  item.onSelect();
                }}
                className={cn(
                  "hover:bg-bank-surface-2 block w-full rounded-md px-2.5 py-1.5 text-left text-[13px]",
                  "disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent",
                  item.danger && "text-bank-bad-ink",
                )}
              >
                {item.label}
                {item.hint && <span className="text-bank-muted block text-[11.5px]">{item.hint}</span>}
              </button>
            ),
          )}
        </div>
      )}
    </>
  );
}
